import { useCallback, type MouseEvent } from "react";
import {
  Box,
  Button,
  Card,
  ContextMenu,
  ContextMenuItem,
  ContextMenuPopup,
  ContextMenuTrigger,
  Icon,
  Text,
  useIsMobile,
} from "@salestrak/ui";

import type { NotificationRow as InboxStandardFragment } from "#app/lib/data";
import { EntityTag } from "#app/components/entity";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";

interface InboxNotificationItemProps {
  item: InboxStandardFragment;
  onClick: (item: InboxStandardFragment) => void;
  onClear: (item: InboxStandardFragment) => void;
  onUnclear: (item: InboxStandardFragment) => void;
  isCleared: boolean;
}

export function InboxNotificationItem({
  item,
  onClick,
  onClear,
  onUnclear,
  isCleared,
}: InboxNotificationItemProps) {
  const isMobile = useIsMobile();
  const author = item.author ?? item.context.author;
  const issue = item.context.issue;
  const isUnread = !item.read;

  const handleClick = useCallback(() => {
    onClick(item);
  }, [item, onClick]);

  const handleClear = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      onClear(item);
    },
    [item, onClear],
  );

  const handleUnclear = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      onUnclear(item);
    },
    [item, onUnclear],
  );

  const timestamp = (
    <Text
      size="xs"
      weight={isUnread ? "semibold" : "normal"}
      variant={isUnread ? "default" : "muted"}
    >
      {formatRelativeDate(item.creationDate, "compact")}
    </Text>
  );

  // The row is clickable but is NOT a button element — it hosts real buttons
  // (clear/unclear), and a button cannot nest inside a button.
  const card = (
    <Card
      // Radius goes through the prop so the inner highlight ring rounds with
      // the card — a `rounded-*` class would leave the ring at the default
      // radius and paint a second set of corners.
      radius="md"
      className="group/row cursor-pointer p-2"
      onClick={handleClick}
    >
      {/* Fixed-height row prevents jumps when toggling time ↔ actions */}
      <Box direction="row" align="center" gap="sm" height={32}>
        {/* Project name — 25% width, truncates */}
        <Box direction="row" align="center" gap="sm" width="25%" minWidth={0}>
          {issue ? (
            <EntityTag
              entity={{ kind: "issue", id: issue.issueId, issue }}
              weight={isUnread ? "semibold" : "regular"}
              mutedLabel={!isUnread}
              interactive={false}
            />
          ) : (
            <Box direction="row" align="center" gap="sm">
              <Icon name="home" size="sm" color="muted" />
              <Text size="sm" variant="muted" truncate>
                Unknown project
              </Text>
            </Box>
          )}
        </Box>

        {/* Author — no truncate */}
        <Box direction="row" align="center" gap="xs" width="15%" minWidth={0}>
          {author ? (
            // Inert: the row card owns navigation, not the author tag.
            <EntityTag
              entity={{ kind: "user", id: author.userId, user: author }}
              interactive={false}
              size="xs"
            />
          ) : (
            <Text size="xs" variant="muted" truncate>
              unknown
            </Text>
          )}
        </Box>

        {/* Summary — fills remaining space, truncates */}
        <Box flex={1} minWidth={0}>
          <Text
            size="sm"
            weight={isUnread ? "semibold" : "normal"}
            variant={isUnread ? "default" : "muted"}
            truncate
          >
            {item.summary ?? ""}
          </Text>
        </Box>

        {/* Right column: time, swapped for the actions on hover. The swap is
            pure CSS — no JS hover state. Touch keeps the time and reaches the
            same actions through the context menu below. */}
        <Box direction="row" align="center" gap="xs">
          {isMobile ? (
            timestamp
          ) : (
            <>
              <Box className="group-hover/row:hidden">{timestamp}</Box>
              <Box
                hidden direction="row"
                align="center"
                gap="xs"
                className="group-hover/row:flex"
              >
                <Button
                  variant="outline"
                  size="icon-sm"
                  disabled
                  aria-label="Archive"
                  onClick={handleClear}
                >
                  <Icon name="archive" size="sm" />
                </Button>
                {isCleared ? (
                  <Button variant="secondary" size="sm" onClick={handleUnclear}>
                    <Icon name="repeat" size="sm" />
                    Unclear
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="icon-sm"
                      disabled
                      aria-label="Snooze"
                      onClick={handleClear}
                    >
                      <Icon name="clock" size="sm" />
                    </Button>
                    <Button size="sm" onClick={handleClear}>
                      <Icon name="check" size="sm" />
                      Clear
                    </Button>
                  </>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Card>
  );

  if (!isMobile) return card;

  return (
    <ContextMenu>
      <ContextMenuTrigger render={card} />
      <ContextMenuPopup>
        {isCleared ? (
          <ContextMenuItem onClick={() => onUnclear(item)}>
            <Icon name="repeat" size="sm" />
            Unclear
          </ContextMenuItem>
        ) : (
          <>
            <ContextMenuItem onClick={() => onClear(item)}>
              <Icon name="check" size="sm" />
              Clear
            </ContextMenuItem>
            <ContextMenuItem disabled onClick={() => onClear(item)}>
              <Icon name="clock" size="sm" />
              Snooze
            </ContextMenuItem>
            <ContextMenuItem disabled onClick={() => onClear(item)}>
              <Icon name="archive" size="sm" />
              Archive
            </ContextMenuItem>
          </>
        )}
      </ContextMenuPopup>
    </ContextMenu>
  );
}
